/**
 * Sessions Module
 *
 * @module sessions
 * @category Sessions
 *
 * Handles multi-device session management:
 * - View all active sessions
 * - Revoke specific session
 * - Revoke all other sessions
 */

import type { HttpClient, ClientResult } from "@amaster.ai/http-client";
import type { Session, SuccessResponse, RevokeAllSessionsResponse } from "../types";

export interface SessionsModuleDeps {
  http: HttpClient;
  storage: { getItem: (key: string) => string | null };
}

export function createSessionsModule(deps: SessionsModuleDeps) {
  const { http, storage } = deps;

  return {
    /**
     * Get current session information
     *
     * @category Sessions
     * @example
     * ```typescript
     * const result = await sessions.getSession();
     * if (result.data) {
     *   console.log("Session ID:", result.data.id);
     *   console.log("Device:", result.data.sessionName);
     *   console.log("IP address:", result.data.ipAddress);
     *   console.log("Location:", result.data.location);
     * }
     * ```
     */
    async getSession(): Promise<ClientResult<Session>> {
      const token = storage.getItem("amaster_access_token");
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      return http.request<Session>({
        url: "/api/auth/sessions/current",
        method: "get",
        headers: { Authorization: `Bearer ${token}` },
      });
    },

    /**
     * Get all active sessions for current user
     *
     * @category Sessions
     * @example
     * ```typescript
     * const result = await sessions.getSessions();
     * if (result.data) {
     *   result.data.forEach(session => {
     *     const status = session.isCurrent ? "Current" : "Other";
     *     console.log(`${session.sessionName} - ${session.ipAddress} - ${status}`);
     *   });
     * }
     * ```
     */
    async getSessions(): Promise<ClientResult<Session[]>> {
      const token = storage.getItem("amaster_access_token");
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      return http.request<Session[]>({
        url: "/api/auth/sessions",
        method: "get",
        headers: { Authorization: `Bearer ${token}` },
      });
    },

    /**
     * Revoke a specific session
     *
     * @category Sessions
     * @example
     * ```typescript
     * await sessions.revokeSession("session-id-123");
     * ```
     */
    async revokeSession(sessionId: string): Promise<ClientResult<SuccessResponse>> {
      const token = storage.getItem("amaster_access_token");
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      if (!sessionId) {
        return {
          data: null,
          error: { message: "Session ID is required", status: 400 },
          status: 400,
        };
      }

      return http.request<SuccessResponse>({
        url: `/api/auth/sessions/${sessionId}`,
        method: "delete",
        headers: { Authorization: `Bearer ${token}` },
      });
    },

    /**
     * Revoke all sessions except current one
     *
     * @category Sessions
     * @example
     * ```typescript
     * const result = await sessions.revokeAllSessions();
     * if (result.data) {
     *   console.log(`Revoked ${result.data.revokedCount} sessions`);
     * }
     * ```
     */
    async revokeAllSessions(): Promise<ClientResult<RevokeAllSessionsResponse>> {
      const token = storage.getItem("amaster_access_token");
      if (!token) {
        return {
          data: null,
          error: { message: "Not authenticated", status: 401 },
          status: 401,
        };
      }

      return http.request<RevokeAllSessionsResponse>({
        url: "/api/auth/sessions",
        method: "delete",
        headers: { Authorization: `Bearer ${token}` },
      });
    },
  };
}

export type SessionsModule = ReturnType<typeof createSessionsModule>;
